import { Text } from '@react-three/drei';
import { invalidate } from '@react-three/fiber';
import { Suspense, useCallback } from 'react';
import { Vector3, LineSegments, Float32BufferAttribute } from 'three';
import { useShallow } from 'zustand/shallow';

import { useCreateFaceDimensioning } from 'app/modules/wallforming/components/scenes/objects/dimensions/hooks/useCreateFaceDimensioning';
import { PlacementDimensions } from 'app/modules/wallforming/components/scenes/objects/dimensions/PlacementDimensions';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { DIMENSION_COLOR } from 'shared/constants';

interface FaceDimensionProps {
    dir: Vector3;
    mid: Vector3;
    selectedPoiIndex: number;
}

export const FaceDimension = ({ dir, mid, selectedPoiIndex }: FaceDimensionProps) => {
    const { workingPlane, buildingColliders } = usePlacementStore(
        useShallow((state) => ({
            workingPlane: state.workingPlane,
            buildingColliders: state.colliders['building']
        }))
    );

    const {
        euler,
        positions: linePositions,
        texts: buildingTexts,
        floorZ
    } = useCreateFaceDimensioning({
        selectedPoiIndex,
        workingPlane,
        buildingColliders,
        dir,
        mid
    });

    const handleLineSegmentsUpdate = useCallback(
        (lineSegments: LineSegments | null) => {
            if (lineSegments && linePositions.length > 0) {
                lineSegments.geometry.setAttribute('position', new Float32BufferAttribute(linePositions, 3));
                lineSegments.geometry.translate(-mid.x, -mid.y, -mid.z);
                lineSegments.geometry.attributes.position.needsUpdate = true;

                const lineDistances: number[] = [];
                for (let i = 0; i < linePositions.length; i += 6) {
                    const start = new Vector3(linePositions[i], linePositions[i + 1], linePositions[i + 2]);
                    const end = new Vector3(linePositions[i + 3], linePositions[i + 4], linePositions[i + 5]);
                    lineDistances.push(0, start.distanceTo(end));
                }

                lineSegments.geometry.setAttribute('lineDistance', new Float32BufferAttribute(lineDistances, 1));
                lineSegments.geometry.attributes.lineDistance.needsUpdate = true;
                lineSegments.computeLineDistances();
                invalidate();
            }
        },
        [linePositions, mid]
    );

    if (linePositions.length === 0) {
        return null;
    }

    return (
        <>
            <lineSegments ref={handleLineSegmentsUpdate}>
                <bufferGeometry />
                <lineDashedMaterial color={DIMENSION_COLOR} dashSize={0.15} gapSize={0.06} />
            </lineSegments>

            <Suspense>
                {buildingTexts.map((t) => (
                    <Text
                        key={t.id}
                        anchorX="center"
                        anchorY="middle"
                        color={t.color}
                        fontSize={t.fontSize}
                        position={t.position.toArray()}
                        rotation={euler.toArray()}
                        onSync={() => invalidate()}
                    >
                        {t.text}
                    </Text>
                ))}
            </Suspense>

            {mid && floorZ != null && <PlacementDimensions floorZ={floorZ} mid={mid} selectedPoiIndex={selectedPoiIndex} />}
        </>
    );
};
